import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Briefcase,
    Users,
    FileText,
    LogOut,
    Plus,
    Search,
    Clock,
    MapPin,
    DollarSign,
    ChevronRight,
    Building2,
    CheckCircle2,
    XCircle,
    Edit3,
    Star,
    Trash2,
    ExternalLink,
    Image as ImageIcon,
    FileEdit,
    X,
    UserCog,
    Lock,
    KeyRound,
    ShieldCheck,
    Eye,
    EyeOff,
    Save,
    AlertCircle,
    UserCheck,
    RefreshCw,
    User
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';

const HRDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'job-portal' | 'dashboard' | 'candidates' | 'cms' | 'profile'>('dashboard');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const [loading, setLoading] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const [jobs, setJobs] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingJob, setEditingJob] = useState<any>(null);

    const [formData, setFormData] = useState({
        title: '',
        title_ar: '',
        department: '',
        department_ar: '',
        location: '',
        location_ar: '',
        type: 'Full-time',
        type_ar: '',
        status: 'Active',
        careerLevel: 'Mid Level',
        careerLevel_ar: '',
        locationType: 'On-site',
        locationType_ar: '',
        skills: '',
        skills_ar: ''
    });
 
    const [cmsData, setCmsData] = useState<any>({});
    const [cmsLoading, setCmsLoading] = useState(false);
    const [isCmsModified, setIsCmsModified] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState<any>(null);

    const [applications, setApplications] = useState<any[]>([]);

    const username = localStorage.getItem('hrUser') || 'HR Manager';

    const [profileData, setProfileData] = useState({ username: username, full_name: '', email: '' });
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const [passData, setPassData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [showCurrentPass, setShowCurrentPass] = useState(false);
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [passSaving, setPassSaving] = useState(false);
    const [passMsg, setPassMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [profileTab, setProfileTab] = useState<'profile' | 'password'>('profile');

    const fetchHRProfile = async () => {
        const token = localStorage.getItem('hrToken');
        if (!token) return;
        try {
            const res = await fetch('/api/admin/profile', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setProfileData({
                    username: data.username || '',
                    full_name: data.full_name || '',
                    email: data.email || ''
                });
            }
        } catch (e) { }
    };

    const handleUpdateHRProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setProfileSaving(true);
        setProfileMsg(null);
        const token = localStorage.getItem('hrToken');
        try {
            const res = await fetch('/api/admin/profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    username: profileData.username,
                    full_name: profileData.full_name,
                    email: profileData.email
                })
            });
            const data = await res.json();
            if (res.ok) {
                if (data.token) {
                    localStorage.setItem('hrToken', data.token);
                    localStorage.setItem('hrUser', data.user?.username || profileData.username);
                }
                setProfileMsg({ type: 'success', text: 'Profile details updated successfully!' });
            } else {
                setProfileMsg({ type: 'error', text: data.message || 'Failed to update profile' });
            }
        } catch (err) {
            setProfileMsg({ type: 'error', text: 'Server connection error' });
        } finally {
            setProfileSaving(false);
        }
    };

    const handleResetHRPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setPassMsg(null);
        if (passData.newPassword !== passData.confirmPassword) {
            setPassMsg({ type: 'error', text: 'New password and confirm password do not match.' });
            return;
        }
        if (passData.newPassword.length < 6) {
            setPassMsg({ type: 'error', text: 'New password must be at least 6 characters.' });
            return;
        }
        setPassSaving(true);
        const token = localStorage.getItem('hrToken');
        try {
            const res = await fetch('/api/admin/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    currentPassword: passData.currentPassword,
                    newPassword: passData.newPassword
                })
            });
            const data = await res.json();
            if (res.ok) {
                setPassMsg({ type: 'success', text: 'Password reset successfully!' });
                setPassData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            } else {
                setPassMsg({ type: 'error', text: data.message || 'Failed to reset password' });
            }
        } catch (err) {
            setPassMsg({ type: 'error', text: 'Server connection error' });
        } finally {
            setPassSaving(false);
        }
    };


    const fetchApplications = async (token: string) => {
        try {
            const res = await fetch('/api/applications', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                console.log("Applications data fetched:", data.length);
                setApplications(data);
            } else {
                console.error("Failed to fetch applications:", res.status);
            }
        } catch (error) {
            console.error('Fetch Apps error:', error);
        }
    };

    const fetchJobs = async () => {
        try {
            const res = await fetch('/api/jobs');
            if (res.ok) {
                const data = await res.json();
                console.log("Jobs data fetched:", data.length);
                setJobs(data);
            }
        } catch (error) {
            console.error('Fetch Jobs error:', error);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('hrToken');
        localStorage.removeItem('hrUser');
        navigate('/hr/login');
    };

    const handleOpenModal = (job: any = null) => {
        if (job) {
            setEditingJob(job);
            setFormData({
                title: job.title,
                title_ar: job.title_ar || '',
                department: job.department,
                department_ar: job.department_ar || '',
                location: job.location,
                location_ar: job.location_ar || '',
                type: job.type,
                type_ar: job.type_ar || '',
                status: job.status,
                careerLevel: job.careerLevel || 'Mid Level',
                careerLevel_ar: job.careerLevel_ar || '',
                locationType: job.locationType || 'On-site',
                locationType_ar: job.locationType_ar || '',
                skills: job.skills || '',
                skills_ar: job.skills_ar || ''
            });
        } else {
            setEditingJob(null);
            setFormData({
                title: '',
                title_ar: '',
                department: '',
                department_ar: '',
                location: '',
                location_ar: '',
                type: 'Full-time',
                type_ar: '',
                status: 'Active',
                careerLevel: 'Mid Level',
                careerLevel_ar: '',
                locationType: 'On-site',
                locationType_ar: '',
                skills: '',
                skills_ar: ''
            });
        }
        setShowModal(true);
    };

    const handleSubmitJob = async (e: React.FormEvent) => {
        e.preventDefault();
        const token = localStorage.getItem('hrToken');
        if (!token) return;

        try {
            const url = editingJob 
                ? `/api/jobs/${editingJob.id}`
                : '/api/jobs';
            const method = editingJob ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setShowModal(false);
                fetchJobs();
            } else {
                alert('Failed to save job');
            }
        } catch (error) {
            console.error('Save job error:', error);
        }
    };

    const fetchCmsContent = async () => {
        try {
            const res = await fetch('/api/content/Careers_Portal');
            if (res.ok) {
                const data = await res.json();
                setCmsData(data);
                setIsCmsModified(false);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleUpdateCmsText = (key: string, value: string) => {
        setCmsData((prev: any) => ({ ...prev, [key]: value }));
        setIsCmsModified(true);
    };

    const handleUpdateCmsTextAr = (key: string, value: string) => {
        setCmsData((prev: any) => ({ ...prev, [`${key}_ar`]: value }));
        setIsCmsModified(true);
    };

    const handleSaveAllCms = async () => {
        setCmsLoading(true);
        try {
            // Prepare data for multi-key save if the backend supports it, 
            // or we loop through keys. Our backend handles one by one or we can refactor.
            // Actually, the backend 'api/content/:section' route handles a whole object.
            
            const payload: any = {};
            // We need to transform cmsData to include ar fields properly for the backend
            // Our backend POST /api/content/:section expects updates as { key: value }
            // But we need to send { content_key, content_value, content_value_ar }
            // So we'll iterate and send individual updates to match the backend expectation
            // OR better, we update the backend to handle a bulk update with AR.
            // Since I updated update-text but not bulk content/:section, I'll do individual updates for safety.
            
            let allSuccess = true;
            for (const key of Object.keys(cmsData)) {
                if (key.endsWith('_ar')) continue;
                
                const response = await fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('hrToken')}`
                    },
                    body: JSON.stringify({
                        section: 'Careers_Portal',
                        content_key: key,
                        content_value: cmsData[key],
                        content_value_ar: cmsData[`${key}_ar`] || null
                    })
                });
                if (!response.ok) allSuccess = false;
            }
            if (allSuccess) {
                setIsCmsModified(false);
                alert('All changes saved successfully!');
            } else {
                alert('Some changes failed to save');
            }
        } catch (err) {
            console.error(err);
            alert('Error saving changes');
        } finally {
            setCmsLoading(false);
        }
    };

    const handleUpdateCmsImage = async (key: string, file: File) => {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('section', 'Careers_Portal');
        formData.append('content_key', key);

        try {
            const res = await fetch('/api/homepage/update-image', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('hrToken')}`
                },
                body: formData
            });
            if (res.ok) {
                const data = await res.json();
                setCmsData({ ...cmsData, [key]: data.imageUrl });
                setIsCmsModified(true);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('hrToken');
        if (!token) {
            navigate('/hr/login');
        } else {
            fetchJobs();
            fetchApplications(token);
            fetchCmsContent(); 
        }
    }, [navigate]);

    useEffect(() => {
        const token = localStorage.getItem('hrToken');
        if (token) {
            if (activeTab === 'dashboard') {
                fetchJobs();
                fetchApplications(token);
                fetchCmsContent();
            } else if (activeTab === 'job-portal') {
                fetchJobs();
            } else if (activeTab === 'candidates') {
                fetchApplications(token);
            } else if (activeTab === 'cms') {
                fetchCmsContent();
            }
        }
    }, [activeTab]);

    const handleDeleteJob = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this job opening?")) return;
        const token = localStorage.getItem('hrToken');
        try {
            const res = await fetch(`/api/jobs/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setJobs(jobs.filter(j => j.id !== id));
            }
        } catch (error) {
            console.error('Delete job error:', error);
        }
    };
    const filteredJobs = jobs.filter(job => {
        const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             job.department.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             job.location.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'All' || job.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    const renderDashboard = () => (
        <div className="p-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl font-black text-slate-800 tracking-tight">System Overview</h1>
                    <p className="text-slate-500 font-medium text-xs mt-1">Real-time performance and application statistics.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div 
                    onClick={() => setActiveTab('candidates')}
                    className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:border-blue-200 cursor-pointer transition-all group"
                >
                    <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Users className="w-8 h-8" />
                    </div>
                    <h3 className="text-slate-400 text-sm font-black uppercase tracking-widest">Total Applications</h3>
                    <p className="text-3xl font-black text-slate-800 mt-2">{applications.length}</p>
                    <div className="mt-4 flex items-center gap-2 text-emerald-600 text-xs font-bold">
                        <ChevronRight className="w-4 h-4" /> 
                        <span>{applications.filter(a => new Date(a.applied_at).toDateString() === new Date().toDateString()).length} new today</span>
                    </div>
                </div>

                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                    <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6">
                        <Briefcase className="w-8 h-8" />
                    </div>
                    <h3 className="text-slate-400 text-sm font-black uppercase tracking-widest">Active Openings</h3>
                    <p className="text-3xl font-black text-slate-800 mt-2">{jobs.filter(j => j.status === 'Active').length}</p>
                    <div className="mt-4 flex items-center gap-2 text-slate-500 text-xs font-bold">
                        <Clock className="w-4 h-4" /> 
                        <span>Across {new Set(jobs.map(j => j.department)).size} departments</span>
                    </div>
                </div>

                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                    <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-6">
                        <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="text-slate-400 text-sm font-black uppercase tracking-widest">Filled Positions</h3>
                    <p className="text-3xl font-black text-slate-800 mt-2">{jobs.filter(j => j.status === 'Closed').length}</p>
                    <div className="mt-4 flex items-center gap-2 text-emerald-600 text-xs font-bold">
                        <ExternalLink className="w-4 h-4" /> 
                        <span>View report</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                    <h2 className="text-xl font-black text-slate-800 mb-6 font-sans">Recent Applications</h2>
                    <div className="space-y-4">
                        {applications.slice(0, 5).map(app => (
                            <div key={app.id} className="flex items-center gap-4 p-4 rounded-2xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center font-black">
                                    {app.name.charAt(0)}
                                </div>
                                <div className="flex-1">
                                    <p className="font-bold text-slate-800">{app.name}</p>
                                    <p className="text-xs text-slate-500 font-medium">Applied for {app.job_title}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{new Date(app.applied_at).toLocaleDateString()}</p>
                                </div>
                            </div>
                        ))}
                        {applications.length === 0 && <p className="text-center py-8 text-slate-400 font-medium">No recent applications</p>}
                    </div>
                    <button onClick={() => setActiveTab('candidates')} className="w-full mt-6 py-4 rounded-xl border-2 border-slate-50 text-sm font-black text-slate-500 hover:bg-slate-50 hover:text-blue-600 transition-all">
                        View All Applications
                    </button>
                </div>

                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                    <h2 className="text-xl font-black text-slate-800 mb-6">Quick CMS</h2>
                    <div className="space-y-6">
                        <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Hero Tagline</h4>
                            <p className="text-sm font-bold text-slate-800 italic mb-4">"{cmsData.hero_title || 'HTG Careers'}"</p>
                            <button onClick={() => setActiveTab('cms')} className="text-xs font-black text-blue-600 hover:underline">Revise Landing Page Content</button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <button onClick={() => handleOpenModal()} className="flex flex-col items-center justify-center p-6 bg-blue-50 text-blue-600 rounded-3xl border border-blue-100 hover:bg-blue-600 hover:text-white transition-all group">
                                <Plus className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
                                <span className="text-xs font-black uppercase tracking-widest">New Job</span>
                            </button>
                            <button onClick={() => setActiveTab('cms')} className="flex flex-col items-center justify-center p-6 bg-indigo-50 text-indigo-600 rounded-3xl border border-indigo-100 hover:bg-indigo-600 hover:text-white transition-all group">
                                <FileEdit className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
                                <span className="text-xs font-black uppercase tracking-widest">Edit CMS</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderJobPortal = () => (
        <div className="p-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 relative">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-slate-800 tracking-tight">Manage Job Portal</h1>
                    <p className="text-slate-500 font-medium mt-1">Create, edit and manage career opportunities across the group.</p>
                </div>
                <button 
                    onClick={() => handleOpenModal()} 
                    className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-2xl font-bold transition-all shadow-xl shadow-blue-200 active:scale-95"
                >
                    <Plus className="w-5 h-5" />
                    <span>Post New Job</span>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                    { label: 'Active Jobs', value: jobs.filter(j => j.status === 'Active').length.toString(), icon: Briefcase, color: 'blue' },
                    { label: 'Total Jobs', value: jobs.length.toString(), icon: Users, color: 'indigo' },
                    { label: 'Pending Reviews', value: '-', icon: Clock, color: 'amber' },
                    { label: 'Positions Filled', value: jobs.filter(j => j.status === 'Closed').length.toString(), icon: CheckCircle2, color: 'emerald' },
                ].map((stat, i) => (
                    <div key={i} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className={`w-12 h-12 bg-${stat.color}-50 text-${stat.color}-600 rounded-2xl flex items-center justify-center mb-4`}>
                            <stat.icon className="w-6 h-6" />
                        </div>
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">{stat.label}</p>
                        <h3 className="text-2xl font-black text-slate-800 mt-1">{stat.value}</h3>
                    </div>
                ))}
            </div>

            {/* Filters & Search */}
            <div className="bg-white p-4 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search jobs, departments or locations..."
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-transparent rounded-2xl focus:bg-white focus:border-blue-200 outline-none transition-all text-sm font-medium"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <select 
                        className="px-5 py-3 bg-white border border-slate-100 rounded-2xl text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors outline-none"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="All">All Status</option>
                        <option value="Active">Active</option>
                        <option value="Closed">Closed</option>
                        <option value="Draft">Draft</option>
                    </select>
                </div>
            </div>

            {/* Jobs List */}
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden min-h-[400px]">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50">
                                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Job Details</th>
                                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Status</th>
                                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Applicants</th>
                                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredJobs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center py-12 text-slate-500 font-medium">No job postings found matching your criteria.</td>
                                </tr>
                            ) : null}
                            {filteredJobs.map((job) => (
                                <tr key={job.id} className="group hover:bg-slate-50/50 transition-colors">
                                    <td className="px-8 py-6">
                                        <div className="flex flex-col">
                                            <span className="text-base font-bold text-slate-800 group-hover:text-blue-600 transition-colors">{job.title}</span>
                                            <div className="flex items-center gap-4 mt-2">
                                                <span className="flex items-center gap-1 text-xs font-medium text-slate-400">
                                                    <Building2 className="w-3 h-3" /> {job.department}
                                                </span>
                                                <span className="flex items-center gap-1 text-xs font-medium text-slate-400">
                                                    <MapPin className="w-3 h-3" /> {job.location}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider ${job.status === 'Active' ? 'bg-emerald-50 text-emerald-600' :
                                                job.status === 'Closed' ? 'bg-red-50 text-red-600' :
                                                    'bg-slate-100 text-slate-500'
                                            }`}>
                                            <div className={`w-1.5 h-1.5 rounded-full ${job.status === 'Active' ? 'bg-emerald-500 animate-pulse' :
                                                    job.status === 'Closed' ? 'bg-red-500' :
                                                        'bg-slate-400'
                                                }`} />
                                            {job.status}
                                        </span>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-3">
                                            <div className="flex -space-x-2">
                                                {[...Array(Math.min(3, job.applicants || 0))].map((_, i) => (
                                                    <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center overflow-hidden">
                                                        <img src={`https://i.pravatar.cc/100?u=${job.id}-${i}`} alt="Avatar" className="w-full h-full object-cover" />
                                                    </div>
                                                ))}
                                                {job.applicants > 3 && (
                                                    <div className="w-8 h-8 rounded-full border-2 border-white bg-blue-50 flex items-center justify-center text-[10px] font-bold text-blue-600">
                                                        +{job.applicants - 3}
                                                    </div>
                                                )}
                                                {(!job.applicants || job.applicants === 0) && (
                                                    <span className="text-sm font-bold text-slate-400">0</span>
                                                )}
                                            </div>
                                            {(job.applicants > 0) && <span className="text-sm font-bold text-slate-600">{job.applicants} Applicants</span>}
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => handleOpenModal(job)} className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all">
                                                <Edit3 className="w-5 h-5" />
                                            </button>
                                             <button onClick={() => handleDeleteJob(job.id)} className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all" title="Delete">
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                            {job.status === 'Active' && (
                                                <button 
                                                    onClick={async () => {
                                                        const token = localStorage.getItem('hrToken');
                                                        await fetch(`/api/jobs/${job.id}`, {
                                                            method: 'PUT',
                                                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                                            body: JSON.stringify({ ...job, status: 'Closed' })
                                                        });
                                                        alert('Position marked as filled.');
                                                        fetchJobs();
                                                    }}
                                                    className="p-2.5 text-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                                                    title="Mark as Filled"
                                                >
                                                    <CheckCircle2 className="w-5 h-5" />
                                                </button>
                                            )}
                                            <a href="/job-portal" target="_blank" rel="noreferrer" className="p-2.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-all">
                                                <ExternalLink className="w-5 h-5" />
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
                    <p className="text-xs font-medium text-slate-500">Showing {filteredJobs.length} of {jobs.length} listings</p>
                </div>
            </div>

            {/* Modal for Job Edit/Create */}
            <AnimatePresence>
                {showModal && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
                    >
                        <motion.div 
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl p-8 max-w-xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto custom-scrollbar"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button 
                                onClick={() => setShowModal(false)}
                                className="absolute top-6 right-6 text-slate-400 hover:text-slate-800"
                            >
                                <XCircle className="w-6 h-6" />
                            </button>
                            <h2 className="text-2xl font-black text-slate-800 mb-6 tracking-tight">
                                {editingJob ? 'Edit Job Posting' : 'Post New Job'}
                            </h2>
                            <form onSubmit={handleSubmitJob} className="space-y-5">
                                <BilingualInput 
                                    label="Job Title"
                                    englishValue={formData.title}
                                    arabicValue={formData.title_ar}
                                    onChangeEnglish={val => setFormData({...formData, title: val})}
                                    onChangeArabic={val => setFormData({...formData, title_ar: val})}
                                    required
                                    placeholder="e.g. Senior Civil Engineer"
                                />
                                <BilingualInput 
                                    label="Department"
                                    englishValue={formData.department}
                                    arabicValue={formData.department_ar}
                                    onChangeEnglish={val => setFormData({...formData, department: val})}
                                    onChangeArabic={val => setFormData({...formData, department_ar: val})}
                                    required
                                    placeholder="e.g. Construction"
                                />
                                <BilingualInput 
                                    label="Location"
                                    englishValue={formData.location}
                                    arabicValue={formData.location_ar}
                                    onChangeEnglish={val => setFormData({...formData, location: val})}
                                    onChangeArabic={val => setFormData({...formData, location_ar: val})}
                                    required
                                    placeholder="e.g. Muscat, Oman"
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Job Type</label>
                                        <select 
                                            value={formData.type} 
                                            onChange={e => setFormData({...formData, type: e.target.value})}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm font-medium"
                                        >
                                            <option>Full-time</option>
                                            <option>Part-time</option>
                                            <option>Contract</option>
                                            <option>Commission</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Status</label>
                                        <select 
                                            value={formData.status} 
                                            onChange={e => setFormData({...formData, status: e.target.value})}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm font-medium"
                                        >
                                            <option>Active</option>
                                            <option>Draft</option>
                                            <option>Closed</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Career Level</label>
                                        <select 
                                            value={formData.careerLevel} 
                                            onChange={e => setFormData({...formData, careerLevel: e.target.value})}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm font-medium"
                                        >
                                            {["Entry", "Junior", "Associate", "Mid Level", "Senior", "Team Leader", "Supervisor", "Manager", "Director", "Executive", "Expert", "Head", "Lead", "Specialist", "Senior Specialist", "Engineer", "Analyst", "Architect"].map(l => (
                                                <option key={l} value={l}>{l}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Location Type</label>
                                        <select 
                                            value={formData.locationType} 
                                            onChange={e => setFormData({...formData, locationType: e.target.value})}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm font-medium"
                                        >
                                            <option>On-site</option>
                                            <option>Remote</option>
                                            <option>Hybrid</option>
                                        </select>
                                    </div>
                                </div>
                                <BilingualInput 
                                    label="Skills Requirement (Comma separated)"
                                    englishValue={formData.skills}
                                    arabicValue={formData.skills_ar}
                                    onChangeEnglish={val => setFormData({...formData, skills: val})}
                                    onChangeArabic={val => setFormData({...formData, skills_ar: val})}
                                    placeholder="e.g. React, Node.js, Leadership"
                                />
                                <div className="pt-4 mt-6 border-t border-slate-100 flex justify-end gap-3">
                                    <button 
                                        type="button" 
                                        onClick={() => setShowModal(false)}
                                        className="px-6 py-3 font-bold text-slate-600 hover:bg-slate-50 rounded-xl transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="px-6 py-3 font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg shadow-blue-200 transition-all active:scale-95"
                                    >
                                        {editingJob ? 'Save Changes' : 'Post Job'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    const renderProfileSection = () => (
        <div className="p-8 max-w-4xl w-full mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div>
                <h1 className="text-3xl font-black text-slate-800 tracking-tight flex items-center gap-3">
                    <KeyRound className="w-8 h-8 text-blue-600" />
                    Reset Portal Password
                </h1>
                <p className="text-slate-500 font-medium mt-1">Change your administrator password to ensure secure portal access.</p>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-6">
                <div>
                    <h2 className="text-xl font-bold text-slate-800">Reset Account Password</h2>
                    <p className="text-xs text-slate-400 mt-1">Enter your current password and choose a new password.</p>
                </div>

                {passMsg && (
                    <div className={`p-4 rounded-2xl text-sm font-medium flex items-center gap-3 ${
                        passMsg.type === 'success'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                        {passMsg.type === 'success' ? <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" /> : <AlertCircle className="w-5 h-5 shrink-0 text-red-600" />}
                        {passMsg.text}
                    </div>
                )}

                <form onSubmit={handleResetHRPassword} className="space-y-6 max-w-xl">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Current Password</label>
                        <div className="relative">
                            <input
                                type={showCurrentPass ? 'text' : 'password'}
                                value={passData.currentPassword}
                                onChange={(e) => setPassData({ ...passData, currentPassword: e.target.value })}
                                required
                                className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm font-medium"
                                placeholder="Enter current password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrentPass(!showCurrentPass)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                            >
                                {showCurrentPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">New Password</label>
                        <div className="relative">
                            <input
                                type={showNewPass ? 'text' : 'password'}
                                value={passData.newPassword}
                                onChange={(e) => setPassData({ ...passData, newPassword: e.target.value })}
                                required
                                className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm font-medium"
                                placeholder="Enter new password (min. 6 chars)"
                            />
                            <button
                                type="button"
                                onClick={() => setShowNewPass(!showNewPass)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                            >
                                {showNewPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Confirm New Password</label>
                        <div className="relative">
                            <input
                                type={showConfirmPass ? 'text' : 'password'}
                                value={passData.confirmPassword}
                                onChange={(e) => setPassData({ ...passData, confirmPassword: e.target.value })}
                                required
                                className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm font-medium"
                                placeholder="Confirm new password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPass(!showConfirmPass)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                            >
                                {showConfirmPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={passSaving}
                        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold px-8 py-3.5 rounded-xl shadow-lg shadow-blue-200 flex items-center gap-2 transition-all active:scale-95"
                    >
                        {passSaving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                        <span>{passSaving ? 'Resetting Password...' : 'Reset Password'}</span>
                    </button>
                </form>
            </div>
        </div>
    );

    const navItems = [
        { id: 'dashboard', label: 'Overview', icon: LayoutDashboard },
        { id: 'job-portal', label: 'Manage Job Portal', icon: Briefcase },
        { id: 'candidates', label: 'Applications', icon: Users },
        { id: 'cms', label: 'Careers Portal CMS', icon: FileEdit },
        { id: 'profile', label: 'Reset Password', icon: KeyRound },
    ];

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            {/* Sidebar */}
            <aside className={`bg-slate-900 flex flex-col shrink-0 transition-all duration-300 ease-in-out z-30 ${isSidebarOpen ? 'w-72' : 'w-24'}`}>
                {/* Logo Area */}
                <div className="p-6 border-b border-slate-800 flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-blue-900/20">
                        <Briefcase className="w-6 h-6 text-white" />
                    </div>
                    {isSidebarOpen && (
                        <div className="overflow-hidden whitespace-nowrap">
                            <h2 className="text-xl font-black text-white tracking-tight">Hamdan HR</h2>
                            <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest">Enterprise Portal</p>
                        </div>
                    )}
                </div>

                {/* Nav Items */}
                <nav className="flex-1 px-4 py-8 space-y-2 overflow-y-auto custom-scrollbar">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id as any)}
                            className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all group ${activeTab === item.id
                                    ? 'bg-blue-600 text-white shadow-xl shadow-blue-900/20'
                                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <item.icon className={`w-6 h-6 shrink-0 ${activeTab === item.id ? 'text-white' : 'group-hover:scale-110 transition-transform'}`} />
                            {isSidebarOpen && <span className="font-bold text-sm tracking-tight">{item.label}</span>}
                            {activeTab === item.id && isSidebarOpen && (
                                <motion.div layoutId="activeNav" className="ml-auto">
                                    <ChevronRight className="w-4 h-4 text-blue-200" />
                                </motion.div>
                            )}
                        </button>
                    ))}
                </nav>

                {/* User Profile / Logout */}
                <div className="p-4 mt-auto border-t border-slate-800">
                    <div 
                        onClick={() => { setActiveTab('profile'); fetchHRProfile(); }}
                        className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer hover:bg-white/10 transition-all ${isSidebarOpen ? 'bg-white/5' : 'justify-center'}`}
                        title="Edit Profile & Password"
                    >
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center font-bold text-white shadow-lg">
                            {username.charAt(0)}
                        </div>
                        {isSidebarOpen && (
                            <div className="overflow-hidden">
                                <p className="text-sm font-black text-white truncate">{username}</p>
                                <p className="text-[10px] text-slate-500 font-bold uppercase">Administrator</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={handleLogout}
                        className={`w-full flex items-center gap-4 p-4 mt-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-2xl transition-all ${!isSidebarOpen && 'justify-center'}`}
                    >
                        <LogOut className="w-6 h-6 shrink-0" />
                        {isSidebarOpen && <span className="font-bold text-sm">Sign Out</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
                <header className="h-20 bg-white border-b border-slate-100 flex items-center justify-between px-8 shrink-0 z-20">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-2 text-slate-400 hover:text-slate-800 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isSidebarOpen ? "M4 6h16M4 12h16M4 18h7" : "M4 6h16M4 12h16M4 18h16"} />
                            </svg>
                        </button>
                        <div className="hidden md:flex h-8 w-px bg-slate-100 mx-2" />
                        <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-500">
                            <Clock className="w-4 h-4 text-blue-500" />
                            <span>HR Portal Connected</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="relative hidden lg:block">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Global Search Jobs..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-blue-200 outline-none transition-all text-xs font-medium w-64"
                            />
                        </div>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto bg-[#f8fafc] custom-scrollbar">
                    {activeTab === 'dashboard' && renderDashboard()}
                    {activeTab === 'job-portal' && renderJobPortal()}
                    {activeTab === 'profile' && renderProfileSection()}
                    {activeTab === 'candidates' && (
                        <div className="p-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 relative">
                            <h1 className="text-3xl font-black text-slate-800 tracking-tight">Applications Received</h1>
                            
                            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden min-h-[400px]">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Applicant</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Job Position</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Experience & Education</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Location</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Applied Date</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Resume</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {applications.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="text-center py-12 text-slate-500 font-medium">No applications found.</td>
                                            </tr>
                                        ) : null}
                                        {applications.map((app) => (
                                            <tr key={app.id} className="group hover:bg-slate-50/50 transition-colors">
                                                <td className="px-8 py-6 font-bold text-slate-800">
                                                    <div>{app.name}</div>
                                                    <div className="text-xs text-slate-400 font-medium mt-1">{app.email}</div>
                                                    <div className="text-[10px] text-slate-400 font-bold">{app.phone}</div>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <span className="font-bold text-slate-800">{app.job_title}</span>
                                                    <div className="text-xs text-slate-400 mt-1">{app.department}</div>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <div className="text-sm font-bold text-slate-700">{app.experience_years || 'N/A'} XP</div>
                                                    <div className="text-[10px] text-slate-500 mt-1 bg-slate-50 px-2 py-0.5 rounded italic">Edu: {app.education || 'N/A'}</div>
                                                    <div className="text-[10px] text-blue-600 font-bold mt-2">
                                                        {app.linkedin && (
                                                            <a href={app.linkedin} target="_blank" rel="noreferrer" className="hover:underline flex items-center gap-1">
                                                                <ExternalLink className="w-3 h-3" /> LinkedIn Profile
                                                            </a>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <div className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1">Expectations</div>
                                                    <div className="text-xs font-bold text-slate-800">Pay: {app.expected_salary || 'N/A'}</div>
                                                    <div className="text-xs font-bold text-slate-600">Notice: {app.notice_period || 'N/A'}</div>
                                                </td>
                                                <td className="px-8 py-6 text-[11px] text-slate-500 max-w-[200px]">
                                                    <div className="font-bold text-slate-400 uppercase text-[9px] mb-1">Cover Letter</div>
                                                    <div className="line-clamp-2 italic mb-2">"{app.cover_letter || 'No message provided.'}"</div>
                                                    <button 
                                                        onClick={() => setShowDetailModal(app)}
                                                        className="text-blue-600 font-bold hover:underline text-[9px] uppercase tracking-wider"
                                                    >
                                                        View Full Info
                                                    </button>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <div className="flex flex-col gap-2">
                                                        <a href={`https://hamdanoman.com${app.resume_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-xl font-bold text-xs transition-all border border-blue-100">
                                                            <FileText className="w-3.5 h-3.5" /> View CV
                                                        </a>
                                                        <button 
                                                            onClick={async () => {
                                                                if (!window.confirm(`Are you sure you want to hire ${app.name} for ${app.job_title}? This will close the position.`)) return;
                                                                const token = localStorage.getItem('hrToken');
                                                                const job = jobs.find((j: any) => j.id === app.job_id);
                                                                if (job) {
                                                                    await fetch(`/api/jobs/${job.id}`, {
                                                                        method: 'PUT',
                                                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                                                        body: JSON.stringify({ ...job, status: 'Closed' })
                                                                    });
                                                                    fetchJobs();
                                                                    alert(`${app.name} marked as hired. Position closed.`);
                                                                }
                                                            }}
                                                            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-xl font-bold text-xs transition-all border border-emerald-100"
                                                        >
                                                            <CheckCircle2 className="w-3.5 h-3.5" /> Hire
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Detail Modal */}
                            {showDetailModal && (
                                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[250] flex items-center justify-center p-4">
                                    <motion.div 
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="bg-white rounded-[2.5rem] w-full max-w-2xl overflow-hidden relative shadow-2xl"
                                    >
                                        <div className="bg-slate-900 px-8 py-6 text-white flex justify-between items-center border-b-8 border-red-600">
                                            <div>
                                                <h3 className="text-2xl font-black uppercase tracking-tighter">Application Details</h3>
                                                <p className="text-red-500 font-bold text-xs uppercase tracking-widest">{showDetailModal.job_title} • {showDetailModal.department}</p>
                                            </div>
                                            <button onClick={() => setShowDetailModal(null)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                                                <X className="w-6 h-6" />
                                            </button>
                                        </div>
                                        <div className="p-10 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar text-slate-800">
                                            <div className="grid grid-cols-2 gap-8">
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Full Name</label>
                                                    <p className="font-bold text-xl">{showDetailModal.name}</p>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Email / Phone</label>
                                                    <p className="font-bold">{showDetailModal.email}</p>
                                                    <p className="text-slate-500">{showDetailModal.phone}</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-8 border-t pt-8">
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Education</label>
                                                    <p className="font-bold">{showDetailModal.education}</p>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Experience</label>
                                                    <p className="font-bold">{showDetailModal.experience_years} Years</p>
                                                </div>
                                            </div>
                                            <div className="border-t pt-8">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Cover Letter Message</label>
                                                <div className="bg-slate-50 p-6 rounded-2xl text-sm leading-relaxed italic border border-slate-100 whitespace-pre-wrap">
                                                    "{showDetailModal.cover_letter || 'No message provided.'}"
                                                </div>
                                            </div>
                                            {showDetailModal.linkedin && (
                                                <div className="border-t pt-8">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">LinkedIn Profile</label>
                                                    <a href={showDetailModal.linkedin} target="_blank" rel="noreferrer" className="text-blue-600 font-bold hover:underline flex items-center gap-2">
                                                        <ExternalLink className="w-4 h-4" /> {showDetailModal.linkedin}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-8 bg-slate-50 flex justify-end gap-4">
                                            <button onClick={() => setShowDetailModal(null)} className="px-8 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-200 transition-colors">Close</button>
                                            <a href={`https://hamdanoman.com${showDetailModal.resume_url}`} target="_blank" rel="noreferrer" className="bg-red-600 text-white px-8 py-3 rounded-xl font-black shadow-lg hover:bg-black transition-all flex items-center gap-2">
                                                <FileText className="w-4 h-4" /> DOWNLOAD CV
                                            </a>
                                        </div>
                                    </motion.div>
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'cms' && (
                        <div className="p-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 relative pb-32">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm sticky top-4 z-50">
                                <div>
                                    <h1 className="text-3xl font-black text-slate-800 tracking-tight">Careers Portal CMS</h1>
                                    <p className="text-slate-500 font-medium mt-1">Manage all landing page content and imagery.</p>
                                </div>
                                {isCmsModified && (
                                    <button 
                                        onClick={handleSaveAllCms} 
                                        disabled={cmsLoading} 
                                        className="bg-emerald-600 hover:bg-black text-white px-10 py-4 rounded-2xl font-black transition-all shadow-xl flex items-center gap-3 border-b-4 border-emerald-800 active:border-b-0 active:translate-y-1"
                                    >
                                        <CheckCircle2 className="w-6 h-6" /> SAVE ALL CHANGES
                                    </button>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Header & Footer Global Content */}
                                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 lg:col-span-2">
                                    <div className="flex items-center justify-between border-b pb-4">
                                        <h2 className="text-xl font-bold flex items-center gap-2 text-slate-800">
                                            <LayoutDashboard className="w-5 h-5 text-blue-500" /> Header & Footer Global Content
                                        </h2>
                                        {isCmsModified && (
                                            <button 
                                                onClick={handleSaveAllCms}
                                                disabled={cmsLoading}
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg flex items-center gap-2 animate-pulse"
                                            >
                                                <CheckCircle2 className="w-4 h-4" /> Save All Changes
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <BilingualInput 
                                            label="Header Navigation Text"
                                            englishValue={cmsData.header_nav_text}
                                            arabicValue={cmsData.header_nav_text_ar}
                                            onChangeEnglish={val => handleUpdateCmsText('header_nav_text', val)}
                                            onChangeArabic={val => handleUpdateCmsTextAr('header_nav_text', val)}
                                            placeholder="Why join Hamdan Trading Group?"
                                        />
                                        <BilingualTextArea 
                                            label="Footer Description"
                                            englishValue={cmsData.footer_desc}
                                            arabicValue={cmsData.footer_desc_ar}
                                            onChangeEnglish={val => handleUpdateCmsText('footer_desc', val)}
                                            onChangeArabic={val => handleUpdateCmsTextAr('footer_desc', val)}
                                            placeholder="At Hamdan Trading Group we believe..."
                                            rows={3}
                                        />
                                        <div className="space-y-4">
                                            <label className="block text-xs font-black text-slate-400 uppercase">Footer Contact Address</label>
                                            <input 
                                                type="text"
                                                value={cmsData.footer_address || ''}
                                                onChange={(e) => handleUpdateCmsText('footer_address', e.target.value)}
                                                placeholder="Muscat, Oman"
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                            />
                                        </div>
                                        <div className="space-y-4">
                                            <label className="block text-xs font-black text-slate-400 uppercase">Footer Contact Phone</label>
                                            <input 
                                                type="text"
                                                value={cmsData.footer_phone || ''}
                                                onChange={(e) => handleUpdateCmsText('footer_phone', e.target.value)}
                                                placeholder="+968 2456 XXXX"
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                            />
                                        </div>
                                        <div className="space-y-4 border-t pt-6">
                                            <label className="block text-xs font-black text-slate-400 uppercase">Header Logo (Transparent PNG recommended)</label>
                                            <div className="flex items-center gap-4">
                                                <img 
                                                    src={cmsData.header_logo ? `https://hamdanoman.com${cmsData.header_logo}` : '/about.jpeg'} 
                                                    className="h-12 w-auto object-contain p-2 bg-slate-900 rounded-lg"
                                                />
                                                <input 
                                                    type="file" 
                                                    onChange={(e) => e.target.files?.[0] && handleUpdateCmsImage('header_logo', e.target.files[0])}
                                                    className="text-[10px]"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-4 border-t pt-6">
                                            <label className="block text-xs font-black text-slate-400 uppercase">Footer Logo (Transparent PNG recommended)</label>
                                            <div className="flex items-center gap-4">
                                                <img 
                                                    src={cmsData.footer_logo ? `https://hamdanoman.com${cmsData.footer_logo}` : '/about.jpeg'} 
                                                    className="h-12 w-auto object-contain p-2 bg-slate-900 rounded-lg"
                                                />
                                                <input 
                                                    type="file" 
                                                    onChange={(e) => e.target.files?.[0] && handleUpdateCmsImage('footer_logo', e.target.files[0])}
                                                    className="text-[10px]"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Hero Section CMS */}
                                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                                    <h2 className="text-xl font-bold flex items-center gap-2 text-slate-800">
                                        <ImageIcon className="w-5 h-5 text-blue-500" /> Hero Section
                                    </h2>
                                    
                                    <div className="space-y-4">
                                        <BilingualInput 
                                            label="Hero Title"
                                            englishValue={cmsData.hero_title}
                                            arabicValue={cmsData.hero_title_ar}
                                            onChangeEnglish={val => handleUpdateCmsText('hero_title', val)}
                                            onChangeArabic={val => handleUpdateCmsTextAr('hero_title', val)}
                                            placeholder="HTG Careers"
                                        />
                                        <BilingualInput 
                                            label="Hero Subtitle"
                                            englishValue={cmsData.hero_subtitle}
                                            arabicValue={cmsData.hero_subtitle_ar}
                                            onChangeEnglish={val => handleUpdateCmsText('hero_subtitle', val)}
                                            onChangeArabic={val => handleUpdateCmsTextAr('hero_subtitle', val)}
                                            placeholder="Take your future in your hands."
                                        />
                                        <div>
                                            <label className="block text-xs font-black text-slate-400 uppercase mb-2">Hero Button Text</label>
                                            <input 
                                                type="text"
                                                value={cmsData.hero_button_text || ''}
                                                onChange={(e) => handleUpdateCmsText('hero_button_text', e.target.value)}
                                                placeholder="Apply Now"
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-400 uppercase mb-2">Hero Background Image</label>
                                            <div className="flex items-center gap-4">
                                                <img 
                                                    src={cmsData.hero_bg ? `https://hamdanoman.com${cmsData.hero_bg}` : '/Careers.jpg'} 
                                                    className="w-20 h-20 object-cover rounded-xl border border-slate-200"
                                                />
                                                <input 
                                                    type="file" 
                                                    onChange={(e) => e.target.files?.[0] && handleUpdateCmsImage('hero_bg', e.target.files[0])}
                                                    className="text-xs"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* About Section CMS */}
                                 <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                                    <div className="flex items-center justify-between border-b pb-4">
                                        <h2 className="text-xl font-bold flex items-center gap-2 text-slate-800">
                                            <FileText className="w-5 h-5 text-blue-500" /> About Content
                                        </h2>
                                        {isCmsModified && (
                                            <button onClick={handleSaveAllCms} disabled={cmsLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-xl font-bold text-sm transition-all shadow-md flex items-center gap-2">
                                                <CheckCircle2 className="w-4 h-4" /> Save changes
                                            </button>
                                        )}
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-black text-slate-400 uppercase mb-2">About Section Tag</label>
                                            <input 
                                                type="text"
                                                value={cmsData.about_tag || ''}
                                                onChange={(e) => handleUpdateCmsText('about_tag', e.target.value)}
                                                placeholder="Our Culture"
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-400 uppercase mb-2">About Section Title</label>
                                            <input 
                                                type="text"
                                                value={cmsData.about_title || ''}
                                                onChange={(e) => handleUpdateCmsText('about_title', e.target.value)}
                                                placeholder="The Human Spirit in Every Trade."
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                            />
                                        </div>
                                        <BilingualTextArea 
                                            label="Main Content Text"
                                            englishValue={cmsData.about_text}
                                            arabicValue={cmsData.about_text_ar}
                                            onChangeEnglish={val => handleUpdateCmsText('about_text', val)}
                                            onChangeArabic={val => handleUpdateCmsTextAr('about_text', val)}
                                            placeholder="At Hamdan Trading Group we are restless..."
                                            rows={4}
                                        />
                                        <div>
                                            <label className="block text-xs font-black text-slate-400 uppercase mb-2">About Section Image</label>
                                            <div className="flex items-center gap-4">
                                                <img 
                                                    src={cmsData.about_image ? `https://hamdanoman.com${cmsData.about_image}` : '/about.jpeg'} 
                                                    className="w-20 h-20 object-cover rounded-xl border border-slate-200"
                                                />
                                                <input 
                                                    type="file" 
                                                    onChange={(e) => e.target.files?.[0] && handleUpdateCmsImage('about_image', e.target.files[0])}
                                                    className="text-xs"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Nationalities Stat</label>
                                                <input 
                                                    type="text"
                                                    value={cmsData.stats_nationalities || ''}
                                                    onChange={(e) => handleUpdateCmsText('stats_nationalities', e.target.value)}
                                                    placeholder="30+"
                                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Years Legend Stat</label>
                                                <input 
                                                    type="text"
                                                    value={cmsData.stats_years || ''}
                                                    onChange={(e) => handleUpdateCmsText('stats_years', e.target.value)}
                                                    placeholder="45+"
                                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-[8px] font-black text-slate-400 uppercase">Spirit Overlay Text (HTML allowed)</label>
                                            <input 
                                                type="text"
                                                value={cmsData.spirit_text || ''}
                                                onChange={(e) => handleUpdateCmsText('spirit_text', e.target.value)}
                                                placeholder="THE SPIRIT<br/><span class='text-yellow-400'>OF HTG</span>"
                                                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 font-mono text-xs"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Special Programmes CMS */}
                                 <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 lg:col-span-2">
                                    <div className="flex items-center justify-between border-b pb-4">
                                        <h2 className="text-xl font-bold flex items-center gap-2 text-slate-800">
                                            <Briefcase className="w-5 h-5 text-blue-500" /> Special Programmes & Vacancies
                                        </h2>
                                        {isCmsModified && (
                                            <button onClick={handleSaveAllCms} disabled={cmsLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-xl font-bold text-sm transition-all shadow-md flex items-center gap-2">
                                                <CheckCircle2 className="w-4 h-4" /> Save changes
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 border-b pb-8">
                                        <div className="space-y-2">
                                            <label className="block text-[8px] font-black text-slate-400 uppercase">Vacancies Section Tag</label>
                                            <input type="text" value={cmsData.vacancies_tag || ''} onChange={(e) => handleUpdateCmsText('vacancies_tag', e.target.value)} placeholder="Your career at Hamdan" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-[8px] font-black text-slate-400 uppercase">Vacancies Section Title</label>
                                            <input type="text" value={cmsData.vacancies_title || ''} onChange={(e) => handleUpdateCmsText('vacancies_title', e.target.value)} placeholder="Active Vacancies" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-4">
                                            <h3 className="font-bold text-red-600 font-black uppercase tracking-widest text-[10px]">Experienced Professionals</h3>
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Title</label>
                                                <input 
                                                    type="text"
                                                    value={cmsData.experienced_title || ''}
                                                    onChange={(e) => handleUpdateCmsText('experienced_title', e.target.value)}
                                                    placeholder="Experienced Professionals"
                                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-red-500"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Description</label>
                                                <textarea 
                                                    rows={3}
                                                    value={cmsData.experienced_desc || ''}
                                                    onChange={(e) => handleUpdateCmsText('experienced_desc', e.target.value)}
                                                    placeholder="Join Hamdan Trading Group as an experienced professional..."
                                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-red-500"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Banner Image</label>
                                                <div className="flex items-center gap-4">
                                                    <img 
                                                        src={cmsData.experienced_image ? `https://hamdanoman.com${cmsData.experienced_image}` : '/contact_banner.jpg'} 
                                                        className="w-16 h-16 object-cover rounded-xl border border-slate-200"
                                                    />
                                                    <input 
                                                        type="file" 
                                                        onChange={(e) => e.target.files?.[0] && handleUpdateCmsImage('experienced_image', e.target.files[0])}
                                                        className="text-[10px]"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <h3 className="font-bold text-blue-600 font-black uppercase tracking-widest text-[10px]">Internship Programme</h3>
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Title</label>
                                                <input 
                                                    type="text"
                                                    value={cmsData.intern_title || ''}
                                                    onChange={(e) => handleUpdateCmsText('intern_title', e.target.value)}
                                                    placeholder="Internship Programme"
                                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Description</label>
                                                <textarea 
                                                    rows={3}
                                                    value={cmsData.intern_desc || ''}
                                                    onChange={(e) => handleUpdateCmsText('intern_desc', e.target.value)}
                                                    placeholder="The HTG Internship programme aims..."
                                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Banner Image</label>
                                                <div className="flex items-center gap-4">
                                                    <img 
                                                        src={cmsData.intern_image ? `https://hamdanoman.com${cmsData.intern_image}` : '/Hamdan-Residential%20Complex.jpg'} 
                                                        className="w-16 h-16 object-cover rounded-xl border border-slate-200"
                                                    />
                                                    <input 
                                                        type="file" 
                                                        onChange={(e) => e.target.files?.[0] && handleUpdateCmsImage('intern_image', e.target.files[0])}
                                                        className="text-[10px]"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                 </div>

                                {/* Testimonial Section CMS */}
                                 <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 lg:col-span-2">
                                    <div className="flex items-center justify-between border-b pb-4">
                                        <h2 className="text-xl font-bold flex items-center gap-2 text-slate-800">
                                            <Users className="w-5 h-5 text-blue-500" /> Testimonial Section
                                        </h2>
                                        {isCmsModified && (
                                            <button onClick={handleSaveAllCms} disabled={cmsLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-xl font-bold text-sm transition-all shadow-md flex items-center gap-2">
                                                <CheckCircle2 className="w-4 h-4" /> Save changes
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Section Tag</label>
                                                    <input type="text" value={cmsData.test_tag || ''} onChange={(e) => handleUpdateCmsText('test_tag', e.target.value)} placeholder="Feedback" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Section Title</label>
                                                    <input type="text" value={cmsData.test_title || ''} onChange={(e) => handleUpdateCmsText('test_title', e.target.value)} placeholder="HERE'S WHAT OUR EMPLOYEES SAY" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Employee Quote</label>
                                                <textarea 
                                                    rows={4}
                                                    value={cmsData.test_quote || ''}
                                                    onChange={(e) => handleUpdateCmsText('test_quote', e.target.value)}
                                                    placeholder="Working in Hamdan Trading Group and being exposed to group companies..."
                                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Employee Name</label>
                                                    <input 
                                                        type="text"
                                                        value={cmsData.test_name || ''}
                                                        onChange={(e) => handleUpdateCmsText('test_name', e.target.value)}
                                                        placeholder="Ahmed Al Harthi"
                                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Position/Department</label>
                                                    <input 
                                                        type="text"
                                                        value={cmsData.test_pos || ''}
                                                        onChange={(e) => handleUpdateCmsText('test_pos', e.target.value)}
                                                        placeholder="Operations Department"
                                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase">Employee Image</label>
                                                <div className="flex flex-col gap-4">
                                                    <img 
                                                        src={cmsData.test_image ? `https://hamdanoman.com${cmsData.test_image}` : '/News/Picture01.jpg'} 
                                                        className="w-full h-48 object-cover rounded-[2rem] border border-slate-200 shadow-inner"
                                                    />
                                                    <input 
                                                        type="file" 
                                                        onChange={(e) => e.target.files?.[0] && handleUpdateCmsImage('test_image', e.target.files[0])}
                                                        className="text-[10px]"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                 <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 lg:col-span-2">
                                    <div className="flex items-center justify-between border-b pb-4">
                                        <h2 className="text-xl font-bold flex items-center gap-2 text-slate-800">
                                            <Star className="w-5 h-5 text-yellow-500" /> Why Join & Benefits
                                        </h2>
                                        {isCmsModified && (
                                            <button onClick={handleSaveAllCms} disabled={cmsLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-xl font-bold text-sm transition-all shadow-md flex items-center gap-2">
                                                <CheckCircle2 className="w-4 h-4" /> Save changes
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Section Tag</label>
                                                    <input type="text" value={cmsData.benefits_tag || ''} onChange={(e) => handleUpdateCmsText('benefits_tag', e.target.value)} placeholder="Benefits" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Section Title</label>
                                                    <input type="text" value={cmsData.benefits_title || ''} onChange={(e) => handleUpdateCmsText('benefits_title', e.target.value)} placeholder="Why join Hamdan Trading Group?" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                </div>
                                            </div>
                                            <div className="space-y-4 pt-4 border-t">
                                                <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Benefit 1</h4>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Title</label>
                                                    <input type="text" value={cmsData.benefit1_title || ''} onChange={(e) => handleUpdateCmsText('benefit1_title', e.target.value)} placeholder="Shape Your Future" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs font-bold" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Description</label>
                                                    <textarea rows={2} value={cmsData.benefit1_desc || ''} onChange={(e) => handleUpdateCmsText('benefit1_desc', e.target.value)} placeholder="Embrace a world of experiences..." className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                </div>
                                            </div>
                                            <div className="space-y-4 pt-4 border-t">
                                                <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Benefit 2</h4>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Title</label>
                                                    <input type="text" value={cmsData.benefit2_title || ''} onChange={(e) => handleUpdateCmsText('benefit2_title', e.target.value)} placeholder="Take The Lead" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs font-bold" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Description</label>
                                                    <textarea rows={2} value={cmsData.benefit2_desc || ''} onChange={(e) => handleUpdateCmsText('benefit2_desc', e.target.value)} placeholder="Innovation is at our heart..." className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="space-y-4">
                                                <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Benefit 3</h4>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Title</label>
                                                    <input type="text" value={cmsData.benefit3_title || ''} onChange={(e) => handleUpdateCmsText('benefit3_title', e.target.value)} placeholder="Be Yourself" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs font-bold" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Description</label>
                                                    <textarea rows={2} value={cmsData.benefit3_desc || ''} onChange={(e) => handleUpdateCmsText('benefit3_desc', e.target.value)} placeholder="Join a dynamic global community..." className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                </div>
                                            </div>
                                            <div className="space-y-4 pt-4 border-t">
                                                <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Benefit 4</h4>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Title</label>
                                                    <input type="text" value={cmsData.benefit4_title || ''} onChange={(e) => handleUpdateCmsText('benefit4_title', e.target.value)} placeholder="Make Your Mark" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs font-bold" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="block text-[8px] font-black text-slate-400 uppercase">Description</label>
                                                    <textarea rows={2} value={cmsData.benefit4_desc || ''} onChange={(e) => handleUpdateCmsText('benefit4_desc', e.target.value)} placeholder="Make an impact on people..." className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                </div>
                                            </div>
                                            <div className="space-y-4 pt-4 border-t">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <label className="block text-[8px] font-black text-slate-400 uppercase">Onboarding Tag</label>
                                                        <input type="text" value={cmsData.start_tag || ''} onChange={(e) => handleUpdateCmsText('start_tag', e.target.value)} placeholder="Onboarding" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="block text-[8px] font-black text-slate-400 uppercase">Onboarding Title</label>
                                                        <input type="text" value={cmsData.start_title || ''} onChange={(e) => handleUpdateCmsText('start_title', e.target.value)} placeholder="GETTING STARTED WITH US" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Legal Notices CMS */}
                                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 lg:col-span-2">
                                    <h2 className="text-xl font-bold flex items-center gap-2 text-slate-800 border-b pb-4">
                                        <FileText className="w-5 h-5 text-blue-500" /> Legal Notices Content
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                        <div className="space-y-4">
                                            <label className="block text-xs font-black text-slate-400 uppercase">Terms & Conditions</label>
                                            <textarea 
                                                rows={10}
                                                defaultValue={cmsData.legal_terms || 'Terms and Conditions for Hamdan Trading Group...'}
                                                onBlur={(e) => handleUpdateCmsText('legal_terms', e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 font-serif text-xs"
                                            />
                                        </div>
                                        <div className="space-y-4">
                                            <label className="block text-xs font-black text-slate-400 uppercase">Privacy Notice</label>
                                            <textarea 
                                                rows={10}
                                                defaultValue={cmsData.legal_privacy || 'Privacy Policy for Hamdan Trading Group...'}
                                                onBlur={(e) => handleUpdateCmsText('legal_privacy', e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 font-serif text-xs"
                                            />
                                        </div>
                                        <div className="space-y-4">
                                            <label className="block text-xs font-black text-slate-400 uppercase">Cookie Notice</label>
                                            <textarea 
                                                rows={10}
                                                defaultValue={cmsData.legal_cookie || 'Cookie Policy for Hamdan Trading Group...'}
                                                onBlur={(e) => handleUpdateCmsText('legal_cookie', e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 font-serif text-xs"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {(!['dashboard', 'job-portal', 'candidates', 'cms'].includes(activeTab)) && (
                        <div className="flex flex-col items-center justify-center h-full text-slate-400 space-y-4">
                            <div className="w-20 h-20 bg-slate-100 rounded-[2rem] flex items-center justify-center">
                                <LayoutDashboard className="w-10 h-10 opacity-20" />
                            </div>
                            <p className="font-bold text-sm uppercase tracking-widest">{activeTab} section coming soon</p>
                        </div>
                    )}
                </div>
            </main>

            {/* Global Floating Save Button (Visible only on CMS tab when modified) */}
            {activeTab === 'cms' && isCmsModified && (
                <div className="fixed bottom-8 right-8 z-[200] animate-bounce">
                    <button 
                        onClick={handleSaveAllCms}
                        disabled={cmsLoading}
                        className="bg-emerald-600 hover:bg-black text-white px-10 py-5 rounded-[2rem] font-black shadow-2xl transition-all flex items-center gap-3 border-4 border-white active:scale-95 text-lg"
                    >
                        <CheckCircle2 className="w-7 h-7" /> SAVE ALL CHANGES
                    </button>
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #cbd5e1;
                }
            `}} />
        </div>
    );
};

export default HRDashboard;
